import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  Building2,
  Shield,
  Menu,
  X,
  ChevronRight,
  Home,
  ChevronLeft,
  Settings,
  Maximize2,
  Minimize2,
  Activity,
  Coins,
  Eraser,
  MapPin,
  BarChart3,
  UserX
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { buildHomeNavigationTarget, resolveActiveSiteCode } from '../../utils/navigationContext';
import { confirmLeave } from '../../pages/admin/chart-management/leaveGuard';
import ThemeToggle from '../common/ThemeToggle';

interface AdminLayoutProps {
  children?: React.ReactNode;
}

type SidebarWidth = 'compact' | 'medium' | 'wide';
type SidebarMode = 'expanded' | 'collapsed';

interface SidebarPreferences {
  isOpen: boolean;
  width: SidebarWidth;
  mode: SidebarMode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();

  // Role detection
  const roleName = String(currentUser?.roleName || currentUser?.role || '').toLowerCase();
  const isSystemAdmin = !!currentUser?.isGlobalAdmin || !!(currentUser as any)?.is_superuser || ['system_admin', 'super_admin'].includes(roleName);
  const isHoAdmin = roleName === 'ho_admin' || roleName === 'company_admin' || (roleName === 'admin' && !currentUser?.siteCode);
  const isSiteAdmin = roleName === 'site_admin' || (roleName === 'admin' && !!currentUser?.siteCode);

  // Load preferences from localStorage
  const loadPreferences = (): SidebarPreferences => {
    try {
      const stored = localStorage.getItem('adminSidebarPreferences');
      if (stored) {
        const prefs = JSON.parse(stored);
        return {
          isOpen: window.innerWidth >= 1024 ? (prefs.isOpen ?? true) : false,
          width: prefs.width || 'medium',
          mode: prefs.mode || 'expanded'
        };
      }
    } catch (e) {
}
    return {
      isOpen: window.innerWidth >= 1024,
      width: 'medium',
      mode: 'expanded'
    };
  };

  const [preferences, setPreferences] = useState<SidebarPreferences>(loadPreferences);
  const [sidebarOpen, setSidebarOpen] = useState(preferences.isOpen);
  const [sidebarWidth, setSidebarWidth] = useState<SidebarWidth>(preferences.width);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(preferences.mode === 'collapsed');

  // Save preferences to localStorage
  const savePreferences = useCallback((prefs: Partial<SidebarPreferences>) => {
    const newPrefs = { ...preferences, ...prefs };
    setPreferences(newPrefs);
    try {
      localStorage.setItem('adminSidebarPreferences', JSON.stringify(newPrefs));
    } catch (e) {
}
  }, [preferences]);


  // Update sidebarOpen when preferences change
  useEffect(() => {
    setSidebarOpen(preferences.isOpen);
  }, [preferences.isOpen]);

  const allMenuItems = [
    {
      title: 'Dashboard',
      icon: LayoutDashboard,
      path: '/admin',
      active: location.pathname === '/admin',
      roles: ['system_admin', 'ho_admin', 'company_admin', 'site_admin'] // All admin roles
    },
    {
      title: 'Customer Management',
      icon: Building2,
      path: '/admin/companies',
      active: location.pathname.startsWith('/admin/companies'),
      roles: ['system_admin'] // Only System Admin can manage customer companies
    },
    {
      title: 'Token Management',
      icon: Coins,
      path: '/admin/tokens',
      active: location.pathname.startsWith('/admin/tokens'),
      roles: ['system_admin'] // Only System Admin can manage tokens
    },
    {
      title: 'Site Management',
      icon: Building2,
      path: '/admin/sites',
      active: location.pathname.startsWith('/admin/sites'),
      roles: ['system_admin', 'ho_admin', 'company_admin'] // Only System Admin and HO Admin can manage sites
    },
    {
      title: 'User Management',
      icon: Users,
      path: '/admin/users',
      active: location.pathname.startsWith('/admin/users'),
      roles: ['system_admin', 'ho_admin', 'company_admin', 'site_admin']
    },
    {
      title: 'Security & Monitoring',
      icon: Shield,
      path: '/admin/security',
      active: location.pathname.startsWith('/admin/security'),
      roles: ['system_admin', 'ho_admin', 'company_admin'] // Only system/HO admin
    },
    {
      title: 'User Activity',
      icon: Activity,
      path: '/admin/user-activity',
      active: location.pathname.startsWith('/admin/user-activity'),
      roles: ['system_admin', 'ho_admin', 'company_admin'] // Only system/HO admin
    },
    {
      title: 'Site Attendance & Proofs',
      icon: MapPin,
      path: '/admin/site-attendance',
      active: location.pathname.startsWith('/admin/site-attendance'),
      roles: ['system_admin', 'ho_admin', 'company_admin', 'site_admin']
    },
    {
      title: 'Widget Management',
      icon: BarChart3,
      path: '/admin/chart-management',
      active: location.pathname.startsWith('/admin/chart-management'),
      roles: ['system_admin', 'ho_admin', 'company_admin', 'site_admin']
    },
    {
      title: 'Data Cleanup',
      icon: Eraser,
      path: '/admin/data-cleanup',
      active: location.pathname.startsWith('/admin/data-cleanup'),
      roles: ['system_admin']
    },
    {
      title: 'Data Deletion Requests',
      icon: UserX,
      path: '/admin/data-deletion-requests',
      active: location.pathname.startsWith('/admin/data-deletion-requests'),
      roles: ['system_admin', 'ho_admin', 'company_admin']
    }
  ];


  // Determine current user's effective role for filtering
  const effectiveRole = isSystemAdmin ? 'system_admin' : isHoAdmin ? 'ho_admin' : isSiteAdmin ? 'site_admin' : 'site_admin';

  const menuItems = allMenuItems.filter(item => item.roles.includes(effectiveRole));

  // Calculate sidebar width based on mode and width preference
  const getSidebarWidth = (): number => {
    if (sidebarCollapsed) return 72; // Icon-only width

    const widths: Record<SidebarWidth, number> = {
      compact: 200,
      medium: 256,
      wide: 320
    };
    return widths[sidebarWidth];
  };

  const handleNavigation = (path: string) => {
    if (!confirmLeave()) return;
    navigate(path);
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
      savePreferences({ isOpen: false });
    }
  };

  const toggleSidebar = () => {
    const newState = !sidebarOpen;
    setSidebarOpen(newState);
    savePreferences({ isOpen: newState });
  };

  const toggleCollapse = () => {
    const newCollapsed = !sidebarCollapsed;
    setSidebarCollapsed(newCollapsed);
    savePreferences({ mode: newCollapsed ? 'collapsed' : 'expanded' });
  };

  const changeWidth = (width: SidebarWidth) => {
    setSidebarWidth(width);
    savePreferences({ width });
  };

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  // Update isDesktop on resize
  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
      if (desktop && preferences.isOpen) {
        setSidebarOpen(true);
      } else if (!desktop) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    return () => window.removeEventListener('resize', handleResize);
  }, [preferences.isOpen]);

  const sidebarWidthValue = getSidebarWidth();

  return (
    <div className="admin-shell min-h-screen">
      {/* Responsive Mobile Header */}
      <div className="lg:hidden fixed top-16 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[#1C4D88]/10 shadow-[0_12px_28px_rgba(28, 77, 136,0.08)]">
        <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3">
          <motion.button
            onClick={toggleSidebar}
            whileTap={{ scale: 0.95 }}
            className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 transition-colors active:scale-95"
            aria-label="Toggle sidebar"
          >
            <AnimatePresence mode="wait">
              {sidebarOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <X size={20} className="sm:w-6 sm:h-6" />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Menu size={20} className="sm:w-6 sm:h-6" />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
          <h1 className="admin-page-title text-[1.15rem] text-[#10213A]">Admin</h1>
          <ThemeToggle size="sm" />
        </div>
      </div>

      <div className="flex pt-28 sm:pt-[7.5rem] lg:pt-16 relative">
        {/* Enhanced Responsive Sidebar */}
        <motion.aside
          initial={false}
          animate={{
            x: isDesktop ? 0 : (sidebarOpen ? 0 : -sidebarWidthValue),
            width: sidebarWidthValue
          }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          className={`${isDesktop ? 'fixed' : 'fixed'} ${isDesktop ? 'top-16 h-[calc(100vh-4rem)]' : 'top-28 sm:top-[7.5rem] h-[calc(100vh-7rem)] sm:h-[calc(100vh-7.5rem)]'} left-0 z-40 admin-shell-sidebar overflow-hidden flex flex-col`}
          style={{ width: `${sidebarWidthValue}px` }}
        >
          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar">
            <div className={`p-3 ${sidebarCollapsed ? 'px-2' : 'sm:p-4 lg:px-4 lg:py-5'}`}>
              {/* Logo/Brand */}
              <div className={`admin-sidebar-brand flex items-center gap-2.5 mb-4 sm:mb-5 px-3 py-2.5 ${sidebarCollapsed ? 'justify-center' : ''}`}>
                <div className="w-8 h-8 flex items-center justify-center rounded flex-shrink-0">
                  <img src="/12376087.png" alt="Admin Panel" className="w-6 h-6 object-contain" />
                </div>
                {!sidebarCollapsed && (
                  <div className="min-w-0 flex-1">
                    <h2 className="text-[0.95rem] font-semibold text-[#10213A] leading-none whitespace-nowrap overflow-hidden text-ellipsis">Admin Panel</h2>
                  </div>
                )}
              </div>

              {/* Navigation */}
              <nav data-tour="admin-nav" className="space-y-1 sm:space-y-2">
                {menuItems.map((item, index) => {
                  // Debug: Log menu items to ensure all are present
                  if (process.env.NODE_ENV === 'development' && index === menuItems.length - 1) {
}
                  const Icon = item.icon;
                  return (
                    <motion.button
                      key={item.path}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      whileHover={sidebarCollapsed ? { scale: 1.05 } : { x: 2 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{
                        delay: index * 0.05,
                        type: "spring",
                        stiffness: 300
                      }}
                      onClick={() => handleNavigation(item.path)}
                      title={sidebarCollapsed ? item.title : ''}
                      style={{
                        color: item.active ? '#001F3D' : '#0A1F36',
                        fontWeight: 700
                      }}
                      className={`admin-sidebar-button w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-2 lg:gap-2.5'} px-2 sm:px-3 py-2.5 sm:py-3 transition-all duration-150 text-[13px] lg:text-sm group relative ${item.active
                        ? 'admin-sidebar-button-active shadow-sm !text-[#002F57]'
                        : 'text-[#0E223A] hover:bg-white/70 hover:text-[#002F57]'
                        }`}
                    >
                      <Icon size={18} className="lg:w-[18px] lg:h-[18px] flex-shrink-0" style={{ color: item.active ? '#001F3D' : '#0A1F36' }} />
                      {!sidebarCollapsed && (
                        <>
                          <span className="font-semibold text-[#0E223A] leading-tight break-words" style={{ color: item.active ? '#001F3D' : '#0A1F36', fontWeight: 700 }}>{item.title}</span>
                          {item.active && (
                            <ChevronRight size={14} className="ml-auto flex-shrink-0 text-[#1C4D88]" />
                          )}
                        </>
                      )}
                      {/* Tooltip for collapsed mode */}
                      {sidebarCollapsed && (
                        <div className="absolute left-full ml-2 px-2 py-1 bg-[#10213A] text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity duration-200">
                          {item.title}
                        </div>
                      )}
                    </motion.button>
                  );
                })}
              </nav>

              {/* Back Button */}
              <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-[#1C4D88]/10">
                <motion.button
                  whileHover={sidebarCollapsed ? { scale: 1.05 } : { x: 2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    if (!confirmLeave()) return;
                    const siteCode = resolveActiveSiteCode({
                      userSiteCode: currentUser?.siteCode,
                      userTenant: currentUser?.tenant,
                      preferStoredContext: true
                    });
                    navigate(
                      buildHomeNavigationTarget({
                        user: currentUser,
                        isGlobalAdmin: !!currentUser?.isGlobalAdmin,
                        siteCode
                      })
                    );
                  }}
                  title={sidebarCollapsed ? 'Back to Site' : ''}
                  style={{ color: '#0A1F36', fontWeight: 700 }}
                  className={`admin-sidebar-button w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-2 sm:gap-3'} px-3 sm:px-4 py-3 rounded-2xl text-[#0B1F36] hover:bg-white/80 hover:text-[#001F3D] transition-all duration-150 text-sm sm:text-base group relative border border-transparent hover:border-[#1C4D88]/10`}
                >
                  <Home size={18} className="sm:w-5 sm:h-5" style={{ color: '#0A1F36' }} />
                  {!sidebarCollapsed && <span className="font-bold text-[#0B1F36]" style={{ color: '#0A1F36', fontWeight: 700 }}>Back to Site</span>}
                  {sidebarCollapsed && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-[#10213A] text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity duration-200">
                      Back to Site
                    </div>
                  )}
                </motion.button>
              </div>
            </div>
          </div>

          {/* Collapse Toggle - Desktop Only */}
          {isDesktop && (
            <div className="border-t border-[#1C4D88]/10 bg-white/50 p-2">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleCollapse}
                className="w-full flex items-center justify-center p-2.5 rounded-2xl text-[#1F3856] hover:bg-white/80 hover:text-[#002F57] transition-all duration-150"
                title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
              >
                {sidebarCollapsed ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
              </motion.button>
            </div>
          )}
        </motion.aside>

        {/* Overlay for mobile */}
        <AnimatePresence>
          {sidebarOpen && !isDesktop && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={toggleSidebar}
              className="lg:hidden fixed inset-0 bg-[#10213A]/28 z-30"
              transition={{ duration: 0.2 }}
            />
          )}
        </AnimatePresence>

        {/* Main Content */}
        <main
          className="flex-1 w-full pt-0 lg:pt-0 transition-all duration-300"
          style={{
            marginLeft: isDesktop ? `${sidebarWidthValue}px` : '0',
            width: isDesktop ? `calc(100% - ${sidebarWidthValue}px)` : '100%'
          }}
        >
          <div className={location.pathname === '/admin/chart-management' ? "h-[calc(100vh-4rem)] p-1.5 sm:p-2 overflow-hidden flex flex-col" : "min-h-[calc(100vh-4rem)] p-4 sm:p-6 lg:p-8"}>
            {children || <Outlet />}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;


